import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

import { AGUAppModuleDefinition, AGUNavTone } from '../../app-surface/app-module-definition';
import { ToneOf } from '../../app-surface/nav-tone';
import { SidebarNavLabel } from './sidebar-nav-label';
import { SidebarWordmark } from './sidebar-wordmark';
import { SubmitSidebarSearch } from './sidebar-search';

/** Makes each sidebar's account menu id unique, so two AGU surfaces open at once never collide on `aria-controls`. */
let nextSidebarId = 0;

/**
 * The member block above the nav rail.
 *
 * `RoleLine` is the mockup's `.em` line ('Research Scientist · NOAA'): null when
 * the signed-in account has no job title or organization to show — staff, or a
 * member whose profile carries neither — and the element is then omitted rather
 * than rendered empty.
 *
 * `ProfileLinkLabel` is the identity block's own link ('View my hub
 * profile', P/Member_Hub.html:1755). It travels WITH the identity rather than as a separate input
 * because it is part of this block and nothing else; the empty string means the
 * host app has no profile module to link to, so the link is left out entirely
 * (a link that navigated nowhere would be worse than no link).
 */
export interface AGUSidebarIdentity { readonly Initials: string; readonly Name: string; readonly RoleLine: string | null; readonly ProfileLinkLabel: string; }
/**
 * The Science Talent Network module under the nav (`.side-stn`, P/Member_Hub.html:1673-1679, 1717). It has no external link: its CTA is an
 * in-app action, emitted as `ProfileRequested`, so the card carries a label rather than a URL.
 */
export interface AGUSidebarPromo { readonly Eyebrow: string; readonly Title: string; readonly Text: string; readonly CtaLabel: string; }

/** Breakpoint below which the sidebar becomes an off-canvas drawer (ui-best-practices §5). */
export const AGU_SIDEBAR_MOBILE_MAX_PX = 768;

/**
 * Left navigation shell for AGU member apps: brand, optional search box,
 * identity block, module nav (hidden modules filtered out), optional promo
 * card, account menu. Desktop: sticky column, collapsible to icons. Mobile:
 * off-canvas drawer that slides over content (never squishes it).
 */
@Component({
    standalone: false,
    selector: 'agu-app-sidebar',
    templateUrl: './app-sidebar.component.html',
    styleUrl: './app-sidebar.component.scss',
    host: { '[class.agu-sidebar--collapsed]': 'collapsed', '[class.agu-sidebar--drawer-open]': 'drawerOpen' },
})
export class AGUAppSidebarComponent {
    /** Ties the account button to its own menu, per instance — same device AGUFilterRailComponent uses for its head id (review M-9). */
    readonly AccountMenuId = `agu-sidebar-account-menu-${(nextSidebarId += 1)}`;

    @Input({ required: true }) BrandName!: string;

    /** The brand name without its redundant leading "AGU " — see `SidebarWordmark`. */
    get Wordmark(): string {
        return SidebarWordmark(this.BrandName);
    }
    @Input() Modules: readonly AGUAppModuleDefinition[] = [];
    @Input() ActiveModuleKey = '';
    @Input() Identity: AGUSidebarIdentity | null = null;
    @Input() Promo: AGUSidebarPromo | null = null;
    @Input() SearchPlaceholder: string | null = null;
    @Input() CanReturnToHost = false;

    @Output() ModuleSelected = new EventEmitter<AGUAppModuleDefinition>();
    @Output() ProfileRequested = new EventEmitter<void>();
    @Output() SearchSubmitted = new EventEmitter<string>();
    @Output() ReturnToHostRequested = new EventEmitter<void>();
    @Output() SignOutRequested = new EventEmitter<void>();

    collapsed = false;
    drawerOpen = false;
    accountOpen = false;
    searchText = '';

    /** Wrapper around the account toggle + menu, for outside-click dismissal. */
    @ViewChild('accountWrapper') private accountWrapper?: ElementRef<HTMLElement>;

    get VisibleModules(): readonly AGUAppModuleDefinition[] {
        return this.Modules.filter((m) => !m.HiddenFromNav);
    }
    IsActive(module: AGUAppModuleDefinition): boolean { return module.Key === this.ActiveModuleKey; }
    /** The nav item's tool colour: its `NavTone`, else its module accent (P/Member_Hub.html:1887-1925) — the shared `ToneOf` rule. */
    ToneOf(module: AGUAppModuleDefinition): AGUNavTone { return ToneOf(module); }
    /** Rail text for one module — its `NavLabel`, else its `Title`. */
    NavLabelOf(module: AGUAppModuleDefinition): string { return SidebarNavLabel(module); }
    ToggleCollapsed(): void { this.collapsed = !this.collapsed; }
    OpenDrawer(): void { this.drawerOpen = true; }
    CloseDrawer(): void { this.drawerOpen = false; }
    ToggleAccount(): void { this.accountOpen = !this.accountOpen; }
    @HostListener('document:keydown.escape') OnEscape(): void {
        if (this.accountOpen) { this.accountOpen = false; return; }   // layered Escape (ui §4)
        if (this.drawerOpen) this.drawerOpen = false;
    }
    /** Close the account menu on any click that lands outside its wrapper. */
    @HostListener('document:click', ['$event']) OnDocumentClick(event: MouseEvent): void {
        if (!this.accountOpen) return;
        const root = this.accountWrapper?.nativeElement;
        const target = event.target;
        if (root && target instanceof Node && root.contains(target)) return;
        this.accountOpen = false;
    }
    SelectModule(module: AGUAppModuleDefinition): void { this.ModuleSelected.emit(module); this.drawerOpen = false; this.accountOpen = false; }
    RequestProfile(): void { this.ProfileRequested.emit(); this.drawerOpen = false; this.accountOpen = false; }
    /** Menu items close the menu first: the host keeps a left surface detached, so an open menu would greet the member on re-entry. */
    ReturnToHost(): void { this.accountOpen = false; this.ReturnToHostRequested.emit(); }
    SignOut(): void { this.accountOpen = false; this.SignOutRequested.emit(); }
    /**
     * The box is a plain `[value]`/`(input)` pair rather than `[(ngModel)]`:
     * `NgModel` writes a programmatic model change back to the DOM from a
     * `Promise.then()` continuation of its own (`NgModel._updateValue`), a later
     * write than the change-detection pass, and on this zoneless host (see
     * `AGURenderService`) nothing guarantees a pass after it (live finding #17).
     */
    OnSearchInput(event: Event): void {
        const target = event.target;
        this.searchText = target instanceof HTMLInputElement ? target.value : '';
    }
    /**
     * Hands a non-empty query on and empties the box.
     *
     * `box` is the input itself (the template's `#searchBox`), written directly
     * — the pattern `AGUSearchHeroComponent.ClearQuery` uses. The `[value]`
     * binding alone cannot clear it: Angular writes a property binding only
     * when the bound value CHANGES, and the 2026-10-05 stage sanity (suite I-1)
     * measured `searchText === ''` against a DOM still showing the query, with
     * `ng.applyChanges` leaving it there — the binding's last written value was
     * already '', so writing '' again was no change at all.
     */
    SubmitSearch(box: HTMLInputElement): void {
        const submission = SubmitSidebarSearch(this.searchText);
        this.searchText = submission.NextText;
        box.value = submission.NextText;
        if (submission.Query === null) return;
        this.SearchSubmitted.emit(submission.Query);
        this.drawerOpen = false;
        this.accountOpen = false;
    }
}
